import { Injectable, inject } from '@angular/core';
import { ILineGraphInputData } from '@cta-analytics-dialog/deliverable-analytics-content/graphs/line-graph/line-graph.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { ITimeline } from '@interfaces/campaign-analytics-api.interface';
import { DateUtilitiesService } from './date-utilities.service';

const END_OF_TIMELINE_BUFFER_MS = 6 * 60 * 1000;

interface IViewCountPoint {
  timestamp: number;
  viewCount: number;
}

@Injectable({
  providedIn: 'root',
})
export class GraphUtilitiesService {
  private readonly dateUtils = inject(DateUtilitiesService);

  public getDotConfiguration(): { symbolSize: number; borderWidth: number; shadowBlur: number } {
    const windowWidth = window.innerWidth;

    if (windowWidth < 1441) {
      return {
        borderWidth: 4,
        shadowBlur: 6,
        symbolSize: 10,
      };
    }

    return {
      borderWidth: 8,
      shadowBlur: 12,
      symbolSize: 20,
    };
  }

  public getInterpolatedViewCount(
    timelineDictionary: { [key: number]: IViewCountPoint },
    chatTimestamp: number,
  ): number {
    let interpolatedViewCount = 0;

    const previousPoint = this.findPreviousPoint(timelineDictionary, chatTimestamp);
    const nextPoint = this.findNextPoint(timelineDictionary, chatTimestamp);

    if (previousPoint && nextPoint) {
      interpolatedViewCount =
        previousPoint.viewCount +
        ((nextPoint.viewCount - previousPoint.viewCount) * (chatTimestamp - previousPoint.timestamp)) /
          (nextPoint.timestamp - previousPoint.timestamp);
    } else if (previousPoint) {
      interpolatedViewCount = previousPoint.viewCount;
    } else if (nextPoint) {
      interpolatedViewCount = nextPoint.viewCount;
    }

    return interpolatedViewCount;
  }

  /**
   * Bounds the raw timeline to the content's execution window: it's widened (never clipped) to the
   * earliest/latest of the timeline data and executionTime/end, and gaps at either edge are filled
   * (0 viewers when nothing was recorded yet, or a flat continuation of the last known count).
   */
  public getBoundedViewerPoints(
    timelineData: ITimeline[],
    executionTime?: string | null,
    end?: string | null,
  ): IViewCountPoint[] {
    const points = timelineData
      .map((timeline) => ({
        timestamp: this.dateUtils.parseDateTimeFromApi(timeline.timestamp).getTime(),
        viewCount: timeline.viewCount,
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    if (!executionTime || !end) {
      return points;
    }

    const executionTimestamp = this.dateUtils.parseDateTimeFromApi(executionTime).getTime();
    const endTimestamp = this.dateUtils.parseDateTimeFromApi(end).getTime();

    const [firstPoint] = points;
    const lastPoint = points[points.length - 1];

    const anchoredPoints = [...points];

    if (!firstPoint || firstPoint.timestamp > executionTimestamp) {
      anchoredPoints.unshift({ timestamp: executionTimestamp, viewCount: 0 });
    }

    const effectiveEndTimestamp = lastPoint
      ? Math.max(endTimestamp, lastPoint.timestamp + END_OF_TIMELINE_BUFFER_MS)
      : endTimestamp;

    if (!lastPoint || lastPoint.timestamp < effectiveEndTimestamp) {
      anchoredPoints.push({ timestamp: effectiveEndTimestamp, viewCount: lastPoint?.viewCount ?? 0 });
    }

    return anchoredPoints;
  }

  public processTimelineData(
    timelineData: ITimeline[],
    executionTime?: string | null,
    end?: string | null,
  ): {
    sortedDictionary: { [key: number]: IViewCountPoint };
  } {
    const anchoredPoints = this.getBoundedViewerPoints(timelineData, executionTime, end);

    const sortedDictionary = anchoredPoints.reduce(
      (dict, point) => {
        dict[point.timestamp] = point;

        return dict;
      },
      {} as { [key: number]: IViewCountPoint },
    );

    return { sortedDictionary };
  }

  public getInterpolatedViewerGraphData(viewerGraphData: ILineGraphInputData[]): ILineGraphInputData[] {
    if (!viewerGraphData || viewerGraphData.length === 0) {
      return [];
    }

    if (viewerGraphData.length === 1) {
      return [viewerGraphData[0]];
    }

    const results: ILineGraphInputData[] = [viewerGraphData[0]];

    for (let i = 0; i < viewerGraphData.length - 1; i++) {
      const p1 = viewerGraphData[i];
      const p2 = viewerGraphData[i + 1];

      const t1 = p1.xAxisValue;
      const y1 = p1.yAxisValue;
      const t2 = p2.xAxisValue;
      const y2 = p2.yAxisValue;

      if (t1 >= t2) {
        if (t1 === t2 && results.length > 0 && results[results.length - 1].xAxisValue === p2.xAxisValue) {
          results[results.length - 1] = p2;
        }

        continue;
      }

      let currentMinuteTimestamp = Math.floor(t1 / (60 * 1000) + 1) * (60 * 1000);

      while (currentMinuteTimestamp < t2) {
        if (results.length === 0 || currentMinuteTimestamp > results[results.length - 1].xAxisValue) {
          const interpolatedValue = y1 + ((y2 - y1) * (currentMinuteTimestamp - t1)) / (t2 - t1);
          results.push({
            id: `interp-${p1.id}-${currentMinuteTimestamp}`,
            status: p1.status,
            xAxisValue: currentMinuteTimestamp,
            yAxisValue: interpolatedValue,
          });
        }

        currentMinuteTimestamp += 60 * 1000;
      }

      if (results.length === 0) {
        results.push(p2);
      } else {
        if (p2.xAxisValue > results[results.length - 1].xAxisValue) {
          results.push(p2);
        } else if (p2.xAxisValue === results[results.length - 1].xAxisValue) {
          results[results.length - 1] = p2;
        }
      }
    }

    return results;
  }

  public getColorByStatus(status: EAnalyticsStatus): string {
    switch (status) {
      case EAnalyticsStatus.ok:
        return '#11851d';
      case EAnalyticsStatus.processing:
        return '#06317a';
      case EAnalyticsStatus.problem:
        return '#ea9946';
      case EAnalyticsStatus.failed:
        return '#d71e29';
      default:
        return '#06317a';
    }
  }

  private findPreviousPoint(dictionary: { [key: number]: IViewCountPoint }, timestamp: number): IViewCountPoint | null {
    const timestamps = Object.keys(dictionary).map(Number);

    let left = 0;
    let right = timestamps.length - 1;
    let bestMatch: number | null = null;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const current = timestamps[mid];

      if (current <= timestamp) {
        bestMatch = current;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return bestMatch !== null ? dictionary[bestMatch] : null;
  }

  private findNextPoint(dictionary: { [key: number]: IViewCountPoint }, timestamp: number): IViewCountPoint | null {
    const timestamps = Object.keys(dictionary).map(Number);

    let left = 0;
    let right = timestamps.length - 1;
    let bestMatch: number | null = null;

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const current = timestamps[mid];

      if (current > timestamp) {
        bestMatch = current;
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }

    return bestMatch !== null ? dictionary[bestMatch] : null;
  }
}
